import {useMemo} from 'react';
import {usePaginationFragment} from 'react-relay';
import {TicketListPaginationFragment} from './useTicketListQuery';
import type {useTicketListQuery_tickets$key} from './__generated__/useTicketListQuery_tickets.graphql';
import type {useTicketListQuery_tickets$data} from './__generated__/useTicketListQuery_tickets.graphql';
import type {Ticket} from '../types/ticket';
import {fromGlobalId} from '../utils/fromGlobalId';

type TicketEdges = NonNullable<useTicketListQuery_tickets$data['tickets']['edges']>;
type TicketNode = NonNullable<NonNullable<TicketEdges[number]>['node']>;

export function mapNodeToTicket(node: TicketNode): Ticket {
	// The Related tab is the only place a project link is created or removed, so the
	// queue reads that relation rather than owning a second source. Links arrive
	// oldest-first, so a ticket carrying more than one project link always resolves
	// to the same one, and to the same one the detail panel shows.
	const projectLink = node.links.find(l => l.linkType === 'project') ?? null;
	return {
		id: fromGlobalId(node.id),
		globalId: node.id,
		slug: node.slug,
		account_id: '',
		requester_id: node.requesterId,
		assignee_id: node.assigneeId ? fromGlobalId(node.assigneeId) : null,
		subject: node.subject,
		description: null,
		status: node.status as Ticket['status'],
		priority: node.priority as Ticket['priority'],
		channel: (node.channel ?? 'manual') as Ticket['channel'],
		created_at: node.createdAt as string,
		updated_at: node.updatedAt as string,
		closed_at: (node.closedAt as string | null) ?? null,
		last_customer_message_at: null,
		requester_name: node.requester?.name ?? null,
		requester_email: node.requester?.email ?? null,
		assignee_name: node.assignee?.fullName ?? null,
		labels: node.labels.map((l: TicketNode['labels'][number]) => ({
			id: l.id,
			name: l.name,
			color: l.color,
		})),
		sla: node.sla ?? null,
		has_project_link: projectLink !== null,
		project_name: projectLink?.targetProject?.name ?? null,
	};
}

export interface UseTicketsResult {
	items: Ticket[];
	hasNextPage: boolean;
	isLoadingNext: boolean;
	loadNext: (count?: number) => void;
}

export function useTickets(queryRef: useTicketListQuery_tickets$key): UseTicketsResult {
	const {data, loadNext, hasNext, isLoadingNext} = usePaginationFragment(TicketListPaginationFragment, queryRef);

	const items = useMemo(
		() =>
			(data.tickets.edges ?? [])
				.map(edge => edge?.node)
				.filter((node): node is TicketNode => node != null)
				.map(mapNodeToTicket),
		[data.tickets.edges]
	);

	return {
		items,
		hasNextPage: hasNext,
		isLoadingNext,
		loadNext: (count = 25) => loadNext(count),
	};
}
